import {Paper} from '@mui/material';
import {styled} from '@mui/material/styles';
import {format} from 'date-fns';
import React, {useState} from 'react';
import {ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Bar} from 'recharts';

import CustomSnackbar from '../../components/CustomSnackBar/CustomSnackBar';
import CustomToolTip from '../../components/CustomToolTip/CustomToolTip';
import {DownloadIcon} from '../../constants/icons';
import {pxToRem} from '../../utils';

const StyledResponsiveContainer = styled(ResponsiveContainer)(({theme}) => ({
  borderRadius: pxToRem(8),
  background: theme.palette.white,
  boxShadow: theme.palette.boxShadow?.type2,
  zIndex: 1,
}));

const StyledDownloadIcon = styled(DownloadIcon)(({theme, color}) => ({
  '& path': {
    fill: theme.palette.primary.main,
  },
  '&:hover': {
    cursor: 'pointer',
  },
  display: 'inline-block',
  position: 'relative',
  top: '-99%',
  right: '-80%',
}));

const CustomTooltip = ({active, payload, label}) => {
  if (active && payload && payload.length) {
    return (
      <Paper elevation={3} sx={{p: 1}}>
        <p style={{fontWeight: 'bold', fontSize: pxToRem(24)}}>{label}</p>
        {payload.map((d) => (
          <p key={d?.dataKey} style={{color: `${d?.stroke || d?.fill}`}}>
            {d?.dataKey}: {d?.value}{' '}
            {d?.payload?.[`${d?.dataKey}-percent`] ? <b>{`(${d?.payload?.[`${d?.dataKey}-percent`]})`}</b> : null}
          </p>
        ))}
      </Paper>
    );
  }

  return null;
};

export default function PlotLineChart({chartData, titleInfo}) {
  const [downloadInfo, setDownloadInfo] = useState(false);

  const downloadResult = () => {
    const blob = new Blob([chartData?.exportData?.data], {type: 'text/csv;charset=utf-8;'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${chartData?.exportData?.title}-${format(new Date(), 'dd/MM/yyyy')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setDownloadInfo(true);
  };
  return (
    <StyledResponsiveContainer width="100%" height={350}>
      <LineChart
        data={chartData?.data || []}
        margin={{
          top: 50,
          right: 30,
          left: 20,
          bottom: 5,
        }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey={chartData?.XAxisKey || ''} />
        <YAxis />
        <Tooltip content={<CustomTooltip />} />
        <Legend />
        {!chartData?.totalCount && !chartData?.percentage
          ? null
          : chartData?.dataKeys?.map((dataKey) => (
              <>
                <Line
                  key={dataKey.key}
                  type="monotone"
                  dataKey={dataKey.key}
                  stroke={dataKey.color}
                  activeDot={{r: 8}}
                  strokeWidth={2}
                />
                <Bar dataKey={dataKey.key} stackId="a" fill={dataKey.color} />
              </>
            ))}
        <text
          x={titleInfo?.xPercent || '10%'}
          y="1%"
          textAnchor="end"
          dominantBaseline="hanging"
          style={{fontWeight: 'bold', fontSize: '24px', fill: '#333'}}
        >
          {' '}
          {titleInfo?.text || ''}
        </text>
        <text
          x="99%"
          y="1%"
          textAnchor="end"
          dominantBaseline="hanging"
          style={{fontWeight: 'bold', fontSize: '16px', fill: '#333'}}
        >
          {chartData.totalCount ? (
            <>Total: {chartData?.totalCount || 0}</>
          ) : (
            <>Total Percentage: {chartData?.percentage || 0} %</>
          )}
        </text>
        {!chartData?.totalCount && !chartData?.percentage ? (
          <text
            x="60%"
            y="50%"
            textAnchor="end"
            dominantBaseline="hanging"
            style={{fontWeight: 'bold', fontSize: '16px', fill: '#333'}}
          >
            No Records Found
          </text>
        ) : null}
      </LineChart>
      {chartData?.exportData && (
        <CustomToolTip title={'Export Data'} placement={'top-end'} arrow={true}>
          <StyledDownloadIcon onClick={downloadResult} />
        </CustomToolTip>
      )}
      {downloadInfo && (
        <CustomSnackbar
          isOpen={downloadInfo}
          autoHideDuration={4000}
          isAlert={true}
          severity={'success'}
          handleClose={() => setDownloadInfo(false)}
          message={'Report Exported Successfully.'}
          anchorOriginVertical={'top'}
        />
      )}
    </StyledResponsiveContainer>
  );
}
